import { useState } from "react";
import { createPortal } from "react-dom";
import { IconX } from "@tabler/icons-react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import NewSelectInput from "../../../components/NewSelectInput";
import { use3PLListData } from "../../../apis/3plApis";
import { useWarehouseListData } from "../../../apis/warehouseApi";
import { useOrderForwardBulkMutation } from "../../../apis/orderApis";
import { useNaqelBulkForwardMutation } from "../../../apis/naqelApis";
import { useAramexBulkForwardMutation } from "../../../apis/aramexApis";
import { useJtBulkForwardMutation } from "../../../apis/jtExpressApis";

interface BulkForwardFormData {
  companyId: string;
  warehouse: string;
  noOfPieces?: number;
}

export default function BulkForwardModal({
  selectedOrders,
  onClose,
  refetch,
}: {
  selectedOrders: any[];
  onClose: () => void;
  refetch?: () => void;
}) {
  const profile = useSelector((state: any) => state.auth.profile);

  const { data: shippingPartners } = use3PLListData();
  const { data: warehouses } = useWarehouseListData();

  const { mutate: orderForwardBulkMutate, isPending: isOrderForwardPending } = useOrderForwardBulkMutation();
  const { mutate: naqelBulkForwardMutate, isPending: isNaqelForwardPending } = useNaqelBulkForwardMutation();
  const { mutate: aramexBulkForwardMutate, isPending: isAramexForwardPending } = useAramexBulkForwardMutation();
  const { mutate: jtBulkForwardMutate, isPending: isJtForwardPending } = useJtBulkForwardMutation();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<BulkForwardFormData>();

  const isPending = isOrderForwardPending || isNaqelForwardPending || isAramexForwardPending || isJtForwardPending;

  const onSubmit = (data: BulkForwardFormData) => {
    if (!selectedOrders || selectedOrders.length === 0) {
      toast.error("Please select at least one order");
      return;
    }

    const warehouseIds = Array.from(new Set(selectedOrders.map((o) => o.warehouseId)));

    if (warehouseIds.length > 1) {
      toast.error("Please select orders from same warehouse");
      return;
    }

    if (data.warehouse && warehouseIds.length === 1 && warehouseIds[0] !== data.warehouse) {
      toast.error("The selected warehouse must match the warehouse of the selected orders");
      return;
    }

    const selected3pl = shippingPartners?.find((p: any) => p.id === data.companyId);
    const companyName = selected3pl?.companyName?.toLowerCase() || "";

    // STATUS VALIDATION
    const allowedStatuses = ["RECEIVE_IN_WAREHOUSE", "SHELVE", "IN_TRANSIT"];
    const invalidOrders = selectedOrders.filter((o) => !allowedStatuses.includes(o.status));

    if (invalidOrders.length > 0) {
      toast.error(`Only Receive in warehouse, Shelve, and In transit orders can be forwarded.`);
      return;
    }

    const orderIds = selectedOrders.map((order: any) => order.id);

    if (companyName.includes("naqel") || companyName.includes("naqel express")) {
      naqelBulkForwardMutate(
        { orderIds, clientId: profile?.id } as any,
        {
          onSuccess: (res: any) => {
            if (res?.success === false) {
              toast.error(res?.message || "Naqel forward failed");
              return;
            }
            toast.success("Naqel forwarding in progress...");
            onClose();
            setTimeout(() => {
              refetch?.();
              toast.success("Orders updated successfully");
            }, 3000);
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.message || error?.message || "Naqel forward failed");
          },
        }
      );
    } else if (companyName.includes("aramex")) {
      aramexBulkForwardMutate(
        {
          orderIds,
          noOfPieces: data.noOfPieces || 1,
          clientId: profile?.id || "",
          warehouse: data.warehouse || "",
        },
        {
          onSuccess: (res: any) => {
            if (res?.success === false) {
              toast.error(res?.message || "Aramex forward failed");
              return;
            }
            toast.success("Aramex forwarding in progress...");
            onClose();
            setTimeout(() => {
              refetch?.();
              toast.success("Orders updated successfully");
            }, 3000);
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.message || error?.message || "Aramex forward failed");
          },
        }
      );
    } else if (companyName.includes("j&t") || companyName.includes("jt express")) {
      jtBulkForwardMutate(
        {
          orderIds,
          clientId: profile?.id,
        },
        {
          onSuccess: (res: any) => {
            if (res?.success === false) {
              toast.error(res?.message || "J&T Express forward failed");
              return;
            }
            toast.success("J&T Express forwarding in progress...");
            onClose();
            setTimeout(() => {
              refetch?.();
              toast.success("Orders updated successfully");
            }, 3000);
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.message || error?.message || "J&T Express forward failed");
          },
        }
      );
    } else {
      orderForwardBulkMutate(
        {
          companyId: data.companyId,
          warehouse: data.warehouse,
          orderIds,
          clientId: profile?.id,
        },
        {
          onSuccess: (res: any) => {
            if (res?.success === false) {
              toast.error(res?.message || "Forward failed");
              return;
            }
            toast.success("Orders forwarding in progress...");
            onClose();
            setTimeout(() => {
              refetch?.();
              toast.success("Orders updated successfully");
            }, 3000);
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.message || error?.message || "Forward failed");
          },
        }
      );
    }
  };

  return createPortal(
    <div className="fixed inset-0 w-screen h-screen bg-black/60 z-50 flex items-center justify-center font-sans">
      <form className="rounded-2xl bg-white overflow-hidden shadow-2xl w-[90vw] sm:w-[600px] max-w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex items-center">
          <div className="bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4 w-full rounded-tl-3xl rounded-tr-[63px]">
            <h2 className="text-2xl font-semibold text-[#212121]">Bulk Forward Order</h2>
          </div>
          <div className="relative">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={38}
              height={37}
              viewBox="0 0 38 37"
              fill="none"
              className="absolute -bottom-[10px] -left-[8px] rotate-[-1.8deg]"
            >
              <path d="M0.5 0L4.86189 10.5496C10.4737 24.1222 22.9306 33.645 37.5 35.5L4.91509 37L0.5 0Z" fill="#F7F6F2" />
            </svg>
            <button
              type="button"
              onClick={onClose}
              className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0 hover:bg-gray-300 transition"
            >
              <IconX size={20} />
            </button>
          </div>
        </div>

        <div className="w-full py-6 px-8 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
          <div className="w-full space-y-4">
            <NewSelectInput
              name="companyId"
              label="Select 3PL Company"
              className="w-full"
              fullWidthDropdown={true}
              placeholder="Select 3PL Company"
              selectOptions={
                shippingPartners?.map((company: any) => ({
                  id: company?.id,
                  value: company?.id,
                  text: company?.companyName,
                })) || []
              }
              register={register}
              validation={{ required: "3PL Company is required" }}
              error={errors.companyId}
              disabled={!shippingPartners || shippingPartners.length === 0}
              value={watch("companyId")}
              setValue={(val: string) => setValue("companyId", val)}
            />
            <NewSelectInput
              name="warehouse"
              label="Select Warehouse"
              className="w-full"
              fullWidthDropdown={true}
              placeholder="Select Warehouse"
              selectOptions={
                warehouses?.warehouses?.map((warehouse: any) => ({
                  id: warehouse?.id,
                  value: warehouse?.id,
                  text: warehouse?.name,
                })) || []
              }
              register={register}
              validation={{ required: "Warehouse is required" }}
              error={errors.warehouse}
              disabled={!warehouses?.warehouses || warehouses?.warehouses?.length === 0}
              value={watch("warehouse")}
              setValue={(val: string) => setValue("warehouse", val)}
            />
          </div>
        </div>

        <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-[30px]">
          <button
            type="submit"
            disabled={isPending}
            className="bg-[#FBD037] hover:bg-yellow-400 text-[#000000] flex items-center rounded-full p-2 px-6 space-x-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm font-medium transition shadow-sm"
          >
            {isPending ? "Forwarding..." : "Submit"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
